import { JOGOS_COPA_2026 } from '../data/copaMundo2026';
import { forcaTime } from '../data/forcaTimesCopa2026';
import type { PlacaresMap } from './copaCalculo';

/**
 * Gera um palpite plausível para um único jogo, baseado na força dos times.
 *
 * Regras (resumo):
 * - Time muito mais forte (diff > 25) nunca perde nem empata.
 * - Diferença grande (15-25) → empate ou vitória do mais forte, nunca derrota dele.
 * - Diferença média (5-15) → maioria favorece o mais forte, mas pode empatar.
 * - Times equilibrados (-5 a 5) → empate ou vitória apertada de qualquer um.
 * - Eventualmente "zebra" (chance pequena) em diferenças médias para variar.
 * - Sempre arredonda para inteiros e limita a 5 gols por time.
 */
export function gerarPalpiteAutoJogo(
  mandanteId: string,
  visitanteId: string,
  rng: () => number = Math.random
): { mandante: number; visitante: number } {
  const fm = forcaTime(mandanteId);
  const fv = forcaTime(visitanteId);
  let diff = fm - fv;

  // 3% de chance de "zebra" (atenua a diferença, vira empate ou vitória apertada
  // do mais fraco) quando ela está entre 5 e 18. Mantém um pouco de variação
  // entre execuções sem ficar gerando resultados absurdos.
  const absDiff0 = Math.abs(diff);
  if (absDiff0 >= 5 && absDiff0 <= 18 && rng() < 0.03) {
    diff = -diff * 0.3; // amenizado, não é vitória do time mais fraco, é mais empate
  }

  // Média de gols esperada por time
  let golsM = fm / 40 + diff / 55;
  let golsV = fv / 40 - diff / 55;

  // Aleatoriedade moderada — gera variação razoável entre execuções sem virar zebra
  golsM += (rng() - 0.5) * 1.2;
  golsV += (rng() - 0.5) * 1.2;

  // 12% de chance de "jogo de poucos gols" (subtrai ~1 de cada)
  if (rng() < 0.12) {
    golsM -= 0.7;
    golsV -= 0.7;
  }
  // 10% de chance de "jogo de muitos gols" (adiciona ~0.7 em quem tem mais)
  if (rng() < 0.1) {
    if (golsM >= golsV) golsM += 0.7;
    else golsV += 0.7;
  }

  let m = Math.max(0, Math.min(5, Math.round(golsM)));
  let v = Math.max(0, Math.min(5, Math.round(golsV)));

  const absDiff = Math.abs(diff);

  // Reforço: time muito mais forte (diff > 25) NÃO pode perder nem empatar
  if (diff > 25 && m <= v) {
    m = Math.max(v + 1, 2);
    if (m > 5) m = 5;
    if (v >= m) v = Math.max(0, m - 2);
  }
  if (diff < -25 && v <= m) {
    v = Math.max(m + 1, 2);
    if (v > 5) v = 5;
    if (m >= v) m = Math.max(0, v - 2);
  }

  // Reforço: diferença grande (15-25) → nunca derrota do mais forte
  if (diff > 15 && diff <= 25 && m < v) {
    [m, v] = [v, m];
  }
  if (diff < -15 && diff >= -25 && v < m) {
    [m, v] = [v, m];
  }

  // Evitar massacres (5x0, 5x1) entre times próximos
  if (absDiff < 20 && Math.abs(m - v) > 3) {
    const max = Math.min(m, v) + 3;
    if (m > max) m = max;
    if (v > max) v = max;
  }

  return { mandante: m, visitante: v };
}

/**
 * Gera palpites para todos os 72 jogos da fase de grupos.
 * Pula jogos que já têm palpite preenchido (se preservarExistentes=true).
 */
export function gerarPalpitesAutomaticos(
  existentes: PlacaresMap = {},
  preservarExistentes = false
): PlacaresMap {
  const out: PlacaresMap = preservarExistentes ? { ...existentes } : {};
  JOGOS_COPA_2026.forEach((jogo) => {
    if (preservarExistentes && out[jogo.id]) return;
    out[jogo.id] = gerarPalpiteAutoJogo(jogo.mandanteId, jogo.visitanteId);
  });
  return out;
}
